import { DialogTrigger } from 'react-aria-components';

import { Button, Group, Popup, Stack, Text } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';

import { useRefundPreorder } from '../api/useRefundPreorder';

interface CancelRefundPreorderDialogProps {
	/** The preorder ID to cancel and refund */
	preorderId: string | null;
	/** The buyer's full name for display in the confirmation message */
	buyerFullName: string;
	/** Whether the dialog is currently open */
	isOpen: boolean;
	/** Callback when dialog open state changes */
	onOpenChange: (open: boolean) => void;
}

/**
 * Dialog for confirming cancellation and refund of a preorder
 */
export const CancelRefundPreorderDialog = ({
	preorderId,
	buyerFullName,
	isOpen,
	onOpenChange,
}: CancelRefundPreorderDialogProps): React.ReactNode => {
	const servicesProviderId = useCurrentServicesProviderId();
	const { mutate: refundPreorder, isPending: isRefunding } = useRefundPreorder();

	const handleRefundConfirm = (): void => {
		if (!preorderId) return;
		refundPreorder(
			{ preorderId, servicesProviderId },
			{ onSettled: () => onOpenChange(false) },
		);
	};

	return (
		<DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
			<Popup title='Cancel & refund preorder'>
				<Stack padding='0' itemsSpacing='24'>
					<Text size='body' color='secondary'>
						Are you sure you want to cancel and refund {buyerFullName}&apos;s
						preorder? This action cannot be undone.
					</Text>
					<Group itemsAlignX='end' itemsSpacing='12'>
						<Button
							variant='secondary'
							size='medium'
							onPress={() => onOpenChange(false)}
							isDisabled={isRefunding}>
							Cancel
						</Button>
						<Button
							variant='filled'
							size='medium'
							onPress={handleRefundConfirm}
							isLoading={isRefunding}>
							Refund
						</Button>
					</Group>
				</Stack>
			</Popup>
		</DialogTrigger>
	);
};
